import React from "react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle, GlassCardFooter } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ApnsConfiguration } from "@/types/apns";
import { mockCustomers } from "@/utils/customerGroups";
import { Pencil, Trash, CheckCircle2, AlertCircle, Clock, XCircle } from "lucide-react";
import { format, differenceInDays, isValid } from "date-fns";

function safeFormatDate(dateStr: string | undefined | null, fmt: string): string {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return isValid(d) ? format(d, fmt) : "N/A";
}

function safeDifferenceInDays(dateStr: string | undefined | null): number {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  return isValid(d) ? differenceInDays(d, new Date()) : Infinity;
}

interface ApnsConfigurationCardProps {
  config: ApnsConfiguration;
  onEdit: () => void;
  onDelete: () => void;
}

export function ApnsConfigurationCard({ config, onEdit, onDelete }: ApnsConfigurationCardProps) {
  const getStatusInfo = () => {
    if (config.status === 'active' && config.certificate_expires_at) {
      const daysUntilExpiry = safeDifferenceInDays(config.certificate_expires_at);
      
      if (daysUntilExpiry < 0) {
        return {
          badge: <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Expired</Badge>,
          color: "text-destructive"
        };
      } else if (daysUntilExpiry <= 60) {
        return {
          badge: <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-600"><AlertCircle className="w-3 h-3" /> Expiring Soon</Badge>,
          color: "text-yellow-600"
        };
      } else {
        return {
          badge: <Badge variant="outline" className="gap-1 border-green-500 text-green-600"><CheckCircle2 className="w-3 h-3" /> Active</Badge>,
          color: "text-green-600"
        };
      }
    } else if (config.status === 'csr_generated') {
      return {
        badge: <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> Pending Upload</Badge>,
        color: "text-muted-foreground"
      };
    } else if (config.status === 'expired') {
      return {
        badge: <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Expired</Badge>,
        color: "text-destructive"
      };
    }
    
    return {
      badge: <Badge variant="outline">Unknown</Badge>,
      color: "text-muted-foreground"
    };
  };

  const statusInfo = getStatusInfo();
  const customerName = config.customer_id 
    ? mockCustomers.find(c => c.id === config.customer_id)?.name || 'Unknown Customer'
    : 'Global (All Customers)';

  return (
    <GlassCard variant="default" className="overflow-hidden">
      <GlassCardHeader className="bg-muted/50">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <GlassCardTitle className="text-lg font-medium">{config.name}</GlassCardTitle>
            {statusInfo.badge}
          </div>
          <div className="flex gap-2">
            <Button 
              type="button"
              variant="ghost" 
              size="sm" 
              onClick={onEdit}
              className="hover:bg-primary/10"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button 
              type="button"
              variant="ghost" 
              size="sm" 
              onClick={onDelete}
              className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
            >
              <Trash className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </GlassCardHeader>
      
      <GlassCardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground">Organization Name</p>
            <p className="font-medium">{config.organization_name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">(From CSR)</p>
          </div>
          <div>
            <p className="text-muted-foreground">Common Name</p>
            <p className="font-medium truncate">{config.common_name}</p>
          </div>
        </div>

        {config.email_address && (
          <div className="text-sm">
            <p className="text-muted-foreground">Apple ID Email</p>
            <p className="font-medium truncate">{config.email_address}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t">
          <div>
            <p className="text-muted-foreground">CSR Generated</p>
            <p className="font-medium">{safeFormatDate(config.csr_generated_at, 'MMM d, yyyy')}</p>
          </div>
          {config.certificate_expires_at && (
            <div>
              <p className="text-muted-foreground">Expires</p>
              <p className={`font-medium ${statusInfo.color}`}>
                {safeFormatDate(config.certificate_expires_at, 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      </GlassCardContent>

      <GlassCardFooter className="bg-muted/20 flex justify-center border-t p-2">
        <span className="text-sm text-muted-foreground">
          Assigned to: {customerName}
        </span>
      </GlassCardFooter>
    </GlassCard>
  );
}
